import axios from "axios";

// APIs that must succeed for login; a failure here before login clears cache
const LOGIN_API_PATHS = [
  "/brand/api/auth/signIn",
  "/brand/api/secure/v1/user-profile",
  "/brand/api/secure/v1/brand-profile",
  "/brand/api/external/v1/host-profile",
];

// APIs that abort login only when the request itself fails (data.error is tolerated)
const LOGIN_REJECT_ONLY_PATHS = [
  "/api/secure/v1/icons",
  "/store/api/secure/v1/brand-locale/",
  "/store/api/secure/v1/storeUser/",
  "/warehouse/api/secure/v1/ware-house-user/",
];

const USER_PROFILE_PATH = "/brand/api/secure/v1/user-profile";
const HOST_PROFILE_PATH = "/brand/api/external/v1/host-profile";

const ALLOWED_ROLES = [
  "ROLE_STORE",
  "ROLE_ADMIN",
  "ROLE_SUPPORT_ADMIN",
  "ROLE_ACCOUNTANT",
  "ROLE_SUB_ADMIN_USER",
  "ROLE_WAREHOUSE",
  "ROLE_FRANCHISE_ADMIN",
];

// device settings that logout keeps, so a failed login keeps them too
const PRESERVED_KEYS = [
  "invoicePrinter",
  "tagPrinter",
  "normalA5A4Printer",
  "storePOSEnabled",
  "invoicePrinterPOSValue",
  "tagPrinterPOSValue",
  "invoicePrinterPOSReceiptTypeValue",
  "tabListView",
  "tabListPickupView",
  "storeChallanView",
  "eWHChallanView",
];

const isBeforeLogin = () => {
  try {
    return !localStorage.getItem("adminToken");
  } catch (error) {
    return true;
  }
};

const matches = (url, paths) =>
  typeof url === "string" && paths.some((path) => url.includes(path));

export const clearLoginCache = async () => {
  try {
    const preserved = {};
    PRESERVED_KEYS.forEach((key) => {
      const value = localStorage.getItem(key);
      if (value !== null) preserved[key] = value;
    });
    localStorage.clear();
    Object.keys(preserved).forEach((key) =>
      localStorage.setItem(key, preserved[key]),
    );
    sessionStorage.clear();
    if ("caches" in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((name) => caches.delete(name)));
    }
  } catch (error) {
    console.log("clear cache error", error);
  }
};

const isRoleDenied = (profile) =>
  !!profile &&
  (!ALLOWED_ROLES.includes(profile.role) ||
    (["ROLE_STORE", "ROLE_WAREHOUSE"].includes(profile.role) &&
      profile.mapped !== true));

// login API urls currently in flight, so failures are matched even when
// another interceptor replaces the error object (e.g. network errors)
const pendingLoginUrls = new Set();

const applyLoginCacheCleaner = () => {
  axios.interceptors.request.use((config) => {
    if (
      isBeforeLogin() &&
      matches(config?.url, [...LOGIN_API_PATHS, ...LOGIN_REJECT_ONLY_PATHS])
    ) {
      pendingLoginUrls.add(config.url);
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => {
      const url = response?.config?.url;
      pendingLoginUrls.delete(url);
      if (!isBeforeLogin()) return response;
      if (matches(url, LOGIN_API_PATHS) && response?.data?.error) {
        clearLoginCache();
      } else if (
        matches(url, [USER_PROFILE_PATH]) &&
        isRoleDenied(response?.data)
      ) {
        clearLoginCache();
      }
      return response;
    },
    (error) => {
      const url = error?.config?.url;
      const isLoginFailure = url
        ? matches(url, [...LOGIN_API_PATHS, ...LOGIN_REJECT_ONLY_PATHS])
        : pendingLoginUrls.size > 0;
      if (url) pendingLoginUrls.delete(url);
      else pendingLoginUrls.clear();
      if (isLoginFailure && isBeforeLogin()) {
        clearLoginCache();
      }
      return Promise.reject(error);
    },
  );

  // fetch: host profile is loaded with fetch on the login page
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const url = typeof input === "string" ? input : input?.url || String(input);
    if (!matches(url, [HOST_PROFILE_PATH]) || !isBeforeLogin()) {
      return originalFetch(input, init);
    }
    try {
      const response = await originalFetch(input, init);
      let failed = !response.ok;
      if (!failed) {
        try {
          const data = await response.clone().json();
          failed = !!data?.error;
        } catch (error) {
          failed = true;
        }
      }
      if (failed) clearLoginCache();
      return response;
    } catch (error) {
      clearLoginCache();
      throw error;
    }
  };
};

export default applyLoginCacheCleaner;
